import {HoistModel, managed, PlainObject, TaskObserver, XH} from '@xh/hoist/core';
import {action, bindable, observableRef, runInAction} from '@xh/hoist/mobx';
import {wait} from '@xh/hoist/promise';
import {createObservableRef} from '@xh/hoist/utils/react';
import {isEmpty, isNil, uniqBy} from 'lodash';
import {AiChatTurn} from '../../../data/AiChatTypes';
import {AiSpecAssistRequest, AiSpecField, AiSpecKind, AiSpecTarget} from '../../../data/AiSpecAssistTypes';
import {SpecModel} from '../SpecModel';
import {RejectedSpecOp} from '../SpecTypes';

export interface SpecAssistantTurn {
    instruction: string;
    explanation?: string;
    /** Description of the applied ops, if any. */
    appliedLabel?: string;
    rejected?: RejectedSpecOp<unknown>[];
    /** What the applied ops left out. */
    warnings?: string[];
    /** What the follow-up did, e.g. the widgets it refined. */
    notes?: string[];
    error?: string;
    /** True while the reply is awaited - the instruction shows at once. */
    pending?: boolean;
}

export interface SpecAssistantConfig {
    kind: AiSpecKind;
    specModel: SpecModel<object, any, object>;
    /** Version of the kind's spec schema. */
    schemaVersion: number;
    /** JSON schema of the result, whose descriptions define the ops - sent with each request (rzx-backend/docs/decisions/0027). */
    outputSchema: object;
    /** The component's data rows - for the row count and sample values. */
    rows?: () => PlainObject[];
    /**
     * What to send as the target, for a host whose spec isn't sent as it is (a dashboard sends a
     * summary of its widgets). Defaults to the spec with the field catalog and sample values.
     */
    target?: () => AiSpecTarget;
    title?: string | (() => string);
    /**
     * Work done after ops were applied, while the request still shows as running - e.g. a dashboard
     * having its new widgets refined. Returns lines to add to the turn.
     */
    followUp?: (applied: unknown[]) => Promise<string[]>;
    /** Shown while the follow-up runs. */
    followUpMessage?: string;
    /** Shown before the first instruction. */
    hint: string;
}

const THINKING = 'Thinking...',
    MAX_HISTORY_TURNS = 6,
    SAMPLE_COUNT = 3,
    SAMPLE_ROWS = 200;

/**
 * AI assistant for one configurable component (grid, chart) or for a dashboard. Sends the instruction
 * with the *current* spec (so it sees and builds on manual edits), and dispatches the returned ops
 * through the SpecModel as a single undoable 'ai' change - the same reducer the configurator uses.
 */
export class SpecAssistantModel extends HoistModel {
    readonly specModel: SpecModel<object, any, object>;
    readonly kind: AiSpecKind;
    readonly hint: string;
    private readonly assistConfig: SpecAssistantConfig;

    @bindable accessor prompt = '';
    @observableRef accessor turns: SpecAssistantTurn[] = [];
    /** Sent with each request - a new one when the history is cleared. */
    private conversationId: string = crypto.randomUUID();
    @managed task = TaskObserver.trackLast({message: THINKING});
    /** The scrollable list of turns - kept scrolled to the latest one. */
    scrollRef = createObservableRef<HTMLDivElement>();

    constructor(config: SpecAssistantConfig) {
        super();
        this.assistConfig = config;
        this.specModel = config.specModel;
        this.kind = config.kind;
        this.hint = config.hint;

        // Turns are replaced (never mutated) on every change - also re-scroll when the list is (re)mounted
        this.addReaction({
            track: () => [this.turns, this.scrollRef.current],
            run: () => this.scrollToBottomAsync()
        });
    }

    get isAvailable(): boolean {
        return XH.aiSpecAssistService.isAvailable;
    }

    get canSubmit(): boolean {
        return !!this.prompt.trim() && !this.task.isPending;
    }

    async submitAsync() {
        const instruction = this.prompt.trim();
        if (!instruction || this.task.isPending) return;

        runInAction(() => (this.prompt = ''));
        await this.askAsync(instruction).linkTo(this.task);
    }

    private async scrollToBottomAsync() {
        await wait();
        const el = this.scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }

    @action
    clearHistory() {
        this.turns = [];
        this.conversationId = crypto.randomUUID();
    }

    /** Send an instruction and apply the ops that come back, as one turn - the turn is also returned. */
    async askAsync(instruction: string): Promise<SpecAssistantTurn> {
        const {specModel, assistConfig: config} = this,
            title = typeof config.title === 'function' ? config.title() : config.title,
            request: AiSpecAssistRequest = {
                kind: config.kind,
                schemaVersion: config.schemaVersion,
                instruction,
                outputSchema: config.outputSchema,
                target: {title, ...(config.target?.() ?? this.buildTarget())},
                history: this.buildHistory(),
                conversationId: this.conversationId
            },
            pending = this.addTurn({instruction, pending: true});

        try {
            const response = await XH.aiSpecAssistService.assistAsync(request),
                ops = response.ops ?? [],
                result = ops.length ? specModel.dispatch(ops, {source: 'ai', label: `AI: ${instruction}`}) : {applied: [], rejected: [], warnings: []};

            let turn = this.replaceTurn(pending, {
                instruction,
                explanation: response.explanation,
                appliedLabel: result.applied.length ? specModel.describeOps(result.applied) : null,
                rejected: result.rejected,
                warnings: result.warnings
            });
            if (config.followUp && result.applied.length) turn = await this.followUpAsync(turn, result.applied);
            return turn;
        } catch (e) {
            return this.replaceTurn(pending, {instruction, error: e.message});
        }
    }

    //------------------------
    // Implementation
    //------------------------
    private async followUpAsync(turn: SpecAssistantTurn, applied: unknown[]): Promise<SpecAssistantTurn> {
        const {task, assistConfig: config} = this;
        task.setMessage(config.followUpMessage ?? 'Working...');
        try {
            const notes = await config.followUp(applied);
            return isEmpty(notes) ? turn : this.replaceTurn(turn, {...turn, notes});
        } catch (e) {
            return this.replaceTurn(turn, {...turn, notes: [`Follow-up failed: ${e.message}`]});
        } finally {
            task.setMessage(THINKING);
        }
    }

    @action
    private addTurn(turn: SpecAssistantTurn): SpecAssistantTurn {
        this.turns = [...this.turns, turn];
        return turn;
    }

    @action
    private replaceTurn(turn: SpecAssistantTurn, next: SpecAssistantTurn): SpecAssistantTurn {
        this.turns = this.turns.map(t => (t === turn ? next : t));
        return next;
    }

    private buildHistory(): AiChatTurn[] {
        return this.turns
            .filter(t => !t.error && !t.pending)
            .slice(-MAX_HISTORY_TURNS)
            .flatMap(t => [
                {role: 'user', text: t.instruction},
                {role: 'assistant', text: [t.explanation, t.appliedLabel ? `(applied: ${t.appliedLabel})` : null].filter(Boolean).join(' ')}
            ]);
    }

    private buildTarget(): AiSpecTarget {
        const {specModel} = this,
            rows = this.assistConfig.rows?.() ?? [];
        return {spec: specModel.spec, fields: this.buildFields(rows), capabilities: specModel.capabilities, rowCount: rows.length};
    }

    /** Catalog fields with a few sample values from the loaded data. */
    private buildFields(rows: PlainObject[]): AiSpecField[] {
        const sample = rows.slice(0, SAMPLE_ROWS);
        return this.specModel.catalog.map(f => {
            const samples = uniqBy(
                sample.map(r => r[f.name]).filter(v => !isNil(v) && v !== ''),
                v => String(v)
            ).slice(0, SAMPLE_COUNT);
            return samples.length ? {...f, sampleValues: samples} : {...f};
        });
    }
}
